<template>
    <div class="wishlist-content-page">
        <div class="wishlist-content-page__nav-section">
            <button class="wishlist-content-page__nav-section-back-cta" aria-label="Back" @click="handleShowWishlistListPage">
                <svg aria-hidden="true">
                    <use xlink:href="#icon-next"></use>
                </svg>
            </button>

            <!-- Breadcrumb -->
            <nav aria-label="Breadcrumb" class="wishlist-content-page__nav-section-breadcrumb">
                <ol>
                    <li><a href="/">Home</a></li>
                    <li><button class="wishlist-content-page__nav-section-breadcrumb-cta" @click="() => showWishlistContentPage(false)">Wishlists</button></li>
                    <li aria-current="page">{{ selectedWishlistDetails.lname }}</li>
                </ol>
            </nav>
        </div>

        <!-- Heading -->
        <div class="wishlist-content-page__header">
            <h3 class="wishlist-content-page__title">{{ selectedWishlistDetails.lname }}</h3>
            <div class="wishlist-content-page__menu-container">
                <!-- If desktop screen then show the menus inline -->
                <div v-if="isDesktopScreen" class="wishlist-content-page__menu">
                    <button class="wishlist-content-page__menu-item" @click="handleShareList" :disabled="!selectedWishlistContent.length">Share List</button>
                    <button class="wishlist-content-page__menu-item" @click="handleDuplicateList" :disabled="!selectedWishlistContent.length">Duplicate List</button>
                    <button class="wishlist-content-page__menu-item" @click="handleDeleteList">Delete List</button>
                </div>

                <!-- If moboile screen then show the menus in tooltip modal -->
                <TooltipModal
                    v-else
                    customClass="bottom"
                    v-model="isOpenTooltipModalExternally"
                >
                    <template #tooltipContent>
                        <div class="tooltip-content" role="tooltip">
                            <button class="wishlist-content-page__tooltip-menu-item" @click="handleShareList" :disabled="!selectedWishlistContent.length">Share List</button>
                            <button class="wishlist-content-page__tooltip-menu-item" @click="handleDuplicateList" :disabled="!selectedWishlistContent.length">Duplicate List</button>
                            <button class="wishlist-content-page__tooltip-menu-item" @click="handleDeleteList">Delete List</button>
                        </div>
                    </template>
                    <button class="wishlist-content-page__menu-dots-cta">
                        <svg class="wishlist-content-page__menu-dots-icon">
                            <use href="#three-dots-menu-icon"></use>
                        </svg>
                    </button>
                </TooltipModal>
            </div>
        </div>

        <!-- Product list section -->
        <WishlistProductList/>

        <!-- Modal -->
        <template v-if="isDesktopScreen">
            <Modal
                v-if="isModalOpen"
                :onCloseModal="handleModalClose"
                :title="getModalTitle()"
                customClass="wishlist-content-page__wishlist-action-modal"
            >
                <DuplicateList
                    v-if="showDuplicateListScreen"
                    :onCancel="handleModalClose"
                    :showWishlistContentPage="showWishlistContentPage"
                    :selectedWishlistDetails="selectedWishlistDetails"
                    :allWishlist="allWishlist"/>
                <DeleteList
                    v-else-if="showDeleteListScreen"
                    :onCancel="handleModalClose"
                    :showWishlistContentPage="showWishlistContentPage"
                    :selectedWishlistDetails="selectedWishlistDetails"/>
                <ShareList
                    v-else-if="showShareListScreen"
                    :onCancel="handleModalClose"
                    :isDesktopScreen="isDesktopScreen"
                    :selectedWishlistDetails="selectedWishlistDetails"/>
            </Modal>
        </template>

        <template v-else>
            <BottomDrawer
                v-if="isModalOpen"
                :title="getModalTitle()"
                :onCloseModal="handleModalClose"
            >
                <DuplicateList
                    v-if="showDuplicateListScreen"
                    :onCancel="handleModalClose"
                    :showWishlistContentPage="showWishlistContentPage"
                    :selectedWishlistDetails="selectedWishlistDetails"
                    :allWishlist="allWishlist"/>
                <DeleteList
                    v-else-if="showDeleteListScreen"
                    :onCancel="handleModalClose"
                    :showWishlistContentPage="showWishlistContentPage"
                    :selectedWishlistDetails="selectedWishlistDetails"/>
                <ShareList
                    v-else-if="showShareListScreen"
                    :onCancel="handleModalClose"
                    :isDesktopScreen="isDesktopScreen"
                    :selectedWishlistDetails="selectedWishlistDetails"/>
            </BottomDrawer>
        </template>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useMediaType } from '../../../../../utils/use-media-type';
import { MEDIA_TYPES } from '../../../../../utils/constants';
import TooltipModal from '../../../../global/tooltip-modal.vue';
import WishlistProductList from './wishlist-product-list.vue';
import Modal from '../../../../global/modal.vue';
import BottomDrawer from '../../../../global/bottom-drawer.vue';
import DuplicateList from '../../atoms/duplicate-list.vue';
import DeleteList from '../../atoms/delete-list.vue';
import ShareList from '../../atoms/share-list.vue';

const props = defineProps({
    showWishlistContentPage: {
        type: Function,
        default: () => { }
    },
});

const store = useStore();
const mediaType = useMediaType();

const isModalOpen = ref(false);
const showShareListScreen = ref(false);
const showDuplicateListScreen = ref(false);
const showDeleteListScreen = ref(false);
const isOpenTooltipModalExternally = ref(false);

const isDesktopScreen = computed(() => [MEDIA_TYPES.TABLET_EXTRA_LARGE, MEDIA_TYPES.DESKTOP, MEDIA_TYPES.DESKTOP_LARGE].includes(mediaType.value));
const selectedWishlistDetails = computed(() => store.state.selectedWishlistDetails);
const selectedWishlistContent = computed(() => selectedWishlistDetails.value?.listcontents);
const allWishlist = computed(() => store.state.wishlistContentDetailsList);

onMounted(() => {
    nextTick(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    });
});

const handleShowWishlistListPage = () => {
    props.showWishlistContentPage(false);
};

const getModalTitle = () => {
    if (showShareListScreen.value) {
        return isDesktopScreen.value ? `Share ${selectedWishlistDetails.value.lname}` : 'Share Wishlist';
    } else if (showDuplicateListScreen.value) {
        return 'Duplicate Wishlist';
    } else {
        return 'Are You Sure?';
    }
};

const handleModalClose = () => {
    isModalOpen.value = false;
    showShareListScreen.value = false;
    showDeleteListScreen.value = false;
    showDuplicateListScreen.value = false;
};

const handleShareList = () => {
    const customerDetails = window?.getCustomerDetails?.();

    if (!customerDetails) {
        showToast({ templateType: 'wishlist-page:sign-in', status: 'error', duration: 5000  });
        return;
    }
    isModalOpen.value = true;
    showShareListScreen.value = true;
    handleCloseWishlistMenu();
};

const handleDeleteList = () => {
    isModalOpen.value = true;
    showDeleteListScreen.value = true;
    handleCloseWishlistMenu();
};

const handleDuplicateList = () => {
    isModalOpen.value = true;
    showDuplicateListScreen.value = true;
    handleCloseWishlistMenu();
};

const handleCloseWishlistMenu = () => {
    isOpenTooltipModalExternally.value = false;
};

const showToast = ({ status, title, message, templateType, customMessageClass, enableElipsis, duration }) => {
    const toastData = {
        status,
        title,
        message,
        templateType,
        customMessageClass,
        enableElipsis,
        duration,
        showInCurrentCursorPosition: false
    };
    
    window.dispatchEvent(new CustomEvent('showToast', { detail: toastData }));
};

</script>